import React, { JSX } from 'react';
import { Download, FileText, Database, Cloud, Sheet, Zap } from 'lucide-react';

interface DownloadOption {
  type: string;
  label: string;
  icon: JSX.Element;
  count: number;
  category?: string;
}

interface DownloadSectionProps {
  downloadOptions: DownloadOption[];
  onDownloadSheet: (type: string, label: string) => void;
  onDownloadAll: () => void;
}

const DownloadSection: React.FC<DownloadSectionProps> = ({
  downloadOptions,
  onDownloadSheet,
  onDownloadAll
}) => {

  const getColorForType = (type: string) => {
    const colors: Record<string, string> = {
      master: 'bg-[#0084CF]',
      databank: 'bg-[#3D9985]',
      bd: 'bg-[#FF884B]',
      ambient: 'bg-[#69951A]',
      meter: 'bg-[#3A7DDA]',
      module_cleaning: 'bg-[#9B59B6]',
      grass_cutting: 'bg-[#27AE60]',
      daily_checklist: 'bg-[#E67E22]',
      weekly_checklist: 'bg-[#F39C12]',
      monthly_checklist: 'bg-[#3498DB]',
      quarterly_checklist: 'bg-[#E74C3C]',
      yearly_checklist: 'bg-[#16A085]'
    };
    return colors[type] || 'bg-gray-500';
  };

  // Group options by category
  const groupedOptions = downloadOptions.reduce((acc, option) => {
    const category = (option as any).category || 'Other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(option);
    return acc;
  }, {} as Record<string, typeof downloadOptions>);

  return (
    <div className="space-y-2 flex flex-col h-full">
      {/* Grouped Download Cards - No Scroll, All Visible */}
      <div className="flex-1 min-h-0 space-y-2 overflow-y-auto">
        {Object.entries(groupedOptions).map(([category, options]) => (
          <div key={category} className="space-y-2">
            <div className="text-xs text-gray-600 uppercase tracking-wide px-1" style={{ fontWeight: 800 }}>
              {category}
            </div>
            {/* Use 3 columns for better space utilization - Compact Design */}
            <div className="grid grid-cols-3 gap-1.5">
              {options.map((option) => (
                <div
                  key={option.type}
                  onClick={() => onDownloadSheet(option.type, option.label)}
                  className="bg-gray-50 rounded-lg border border-gray-200 p-1.5 hover:shadow-md hover:border-[#0084CF] transition-all duration-200 cursor-pointer group"
                >
                  <div className="text-center">
                    <div className={`${getColorForType(option.type)} w-9 h-9 rounded-lg flex items-center justify-center mx-auto mb-1 text-white group-hover:scale-110 transition-transform duration-200 shadow-sm`}>
                      {React.cloneElement(option.icon, { size: 14 })}
                    </div>

                    <h3
                      className="mb-0.5 leading-tight line-clamp-2 min-h-[2.2em]"
                      style={{
                        color: '#000000',
                        fontWeight: 700,
                        fontSize: '9px',
                        fontFamily: 'inherit'
                      }}
                    >
                      {option.label}
                    </h3>

                    <div className="bg-[#CBDDE9] text-gray-900 text-[7px] px-1 py-0.5 rounded-full mb-1" style={{ fontWeight: 700 }}>
                      {option.count} Rows
                    </div>

                    <button className="w-full bg-gray-100 hover:bg-[#0084CF] hover:text-white text-gray-800 py-1 px-1 rounded-lg text-[8px] font-semibold transition-all duration-200">
                      <Download size={9} className="inline mr-0.5" />
                      <span className="hidden sm:inline">Download</span>
                      <span className="sm:hidden">DL</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Complete Workbook - Enhanced */}
      <div className="bg-gradient-to-r from-[#CBDDE9] to-[#89BC91] rounded-lg p-3 border-2 border-gray-300 flex-shrink-0 shadow-sm">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="p-2 bg-white rounded-lg flex-shrink-0 shadow-sm">
              <FileText size={18} className="text-[#0084CF]" />
            </div>
            <div className="min-w-0">
              <h3 className="text-sm text-gray-900" style={{ fontWeight: 800 }}>Generation Reports</h3>
              <p className="text-[11px] text-gray-700 truncate" style={{ fontWeight: 700 }}>Download into a single Excel file</p>
            </div>
          </div>

          <button
            onClick={onDownloadAll}
            className="bg-[#0084CF] hover:bg-[#0066A3] text-white px-4 py-2 rounded-lg font-semibold text-xs transition-all duration-200 shadow-md hover:shadow-lg flex-shrink-0 flex items-center gap-1.5"
          >
            <Download size={16} />
            <span>All</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default DownloadSection;
